import type { ContractorJobRunbook } from "@contractor-quoting/shared-types";

export interface RunbookDiagnostic {
  code: string;
  severity: "warning" | "error";
  subject: string;
  message: string;
}

export type AutomationStatus = "automated" | "manual" | "needs_pricing";

export interface RunbookSummary {
  canonicalJobType: string;
  trade: string;
  label: string;
  automationStatus: AutomationStatus;
  state: "baseline" | "draft" | "active";
  version: number;
  versionId: string | null;
  active: boolean;
  updateAvailable: boolean;
  diagnostics: RunbookDiagnostic[];
}

export interface RunbookDetail extends RunbookSummary {
  revision: number;
  definition: ContractorJobRunbook;
}

export interface SharedRate {
  id: string;
  tradeCode: string;
  code: string;
  category: "material" | "labour" | "removal" | "disposal" | "travel" | "consumables" | "fee";
  description: string;
  unit: "square_foot" | "linear_foot" | "hour" | "item" | "fixed";
  quantitySource: string;
  conditionKey: string | null;
  conditionOperator: "equals" | "not_equals" | "includes" | "truthy" | null;
  conditionValue: string | null;
  amountMinor: string;
  priceBasis: "cost" | "sell_price" | "fixed_package";
  taxClass: string;
  taxable: boolean;
  measurementAllowanceBps: number;
  materialWasteBps: number;
  markupApplies: boolean;
  contingencyApplies: boolean;
}

export interface PricingSettings {
  currency: string;
  measurementAllowanceBps: number;
  materialWasteBps: number;
  markupBps: number;
  contingencyBps: number;
  minimumChargeMinor: string;
  taxBps: number;
  taxLabel: string;
}

export interface SharedPricingResponse {
  current: null | {
    bookId: string;
    versionId: string;
    name: string;
    version: number;
    status: "draft" | "published" | "retired";
    active: boolean;
    revision: number;
    settings: PricingSettings;
    rates: SharedRate[];
  };
  versions: Array<{ id: string; version: number; status: string; active: boolean }>;
}

export const automationLabels: Record<AutomationStatus, string> = {
  automated: "Automated pricing",
  manual: "Manual pricing",
  needs_pricing: "Needs pricing",
};

export function moneyFromMinor(value: string): string {
  return (Number(value || 0) / 100).toFixed(2);
}

export function minorFromMoney(value: string): string {
  return String(Math.max(0, Math.round(Number(value || 0) * 100)));
}
